/**
 * Личный макет ленты на /ux. Сокета нет, цифры фейковые, в кассу не ходим.
 * Сюда смотрим, когда решаем, как вилочнику удобнее видеть ставку.
 */

import { useCallback, useState } from 'react';

import { uxCardHref } from '../next/route';
import { UX_FORKS, type UxFork, type UxLeg } from './data';
import './ux.css';

const rub = (n: number): string =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);

const copy = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Макет: буфер может быть закрыт, экран всё равно жив.
  }
};

function Mark({ code, name }: { code: string; name: string }): React.ReactElement {
  return (
    <b title={code} className="ux-mark">
      {name}
    </b>
  );
}

function LegTile({
  n,
  leg,
  done,
  onOpen,
}: {
  n: number;
  leg: UxLeg;
  done: boolean;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <article className="ux-leg">
      <div className="ux-leg-top">
        <span className="ux-n">{n}</span>
        <Mark code={leg.source} name={leg.name} />
      </div>
      <div className="ux-pick">{leg.pick}</div>
      <div className="ux-odds">
        {leg.cents !== undefined ? `цена ${leg.cents} ¢ · кэф ${leg.odds}` : `кэф ${leg.odds}`}
      </div>
      <div className="ux-enter">
        <label>введи в кассу</label>
        <button
          type="button"
          title="скопировать"
          onClick={() => {
            void copy(leg.enter);
          }}
        >
          <strong>{leg.enter}</strong>
          <u>{leg.enterUnit}</u>
        </button>
      </div>
      <p className="ux-note">{leg.cost ?? ''}</p>
      <button type="button" className={`ux-go${done ? ' done' : ''}`} onClick={onOpen}>
        {done ? 'поставил' : `открыть ${leg.name}`}
      </button>
    </article>
  );
}

function Card({
  fork,
  onToast,
}: {
  fork: UxFork;
  onToast: (text: string) => void;
}): React.ReactElement {
  const [done, setDone] = useState<[boolean, boolean]>([false, false]);
  const mark = (i: 0 | 1): void => {
    void copy(fork.legs[i].enter);
    setDone((prev) => {
      const next: [boolean, boolean] = [...prev];
      next[i] = true;
      return next;
    });
    onToast('число скопировано · в макете вкладка не открывается');
  };
  return (
    <div className="ux-card">
      <div className="ux-pair">
        <LegTile n={1} leg={fork.legs[0]} done={done[0]} onOpen={() => mark(0)} />
        <LegTile n={2} leg={fork.legs[1]} done={done[1]} onOpen={() => mark(1)} />
      </div>
      <p className="ux-hint">
        крупное число — то, что вбиваешь. кнопка копирует его и в макете ничего
        не открывает. «поставил» — только отметка для себя.
      </p>
    </div>
  );
}

function Row({
  fork,
  open,
  onOpen,
  onToast,
}: {
  fork: UxFork;
  open: boolean;
  onOpen: () => void;
  onToast: (text: string) => void;
}): React.ReactElement {
  return (
    <div>
      <button type="button" className={`ux-row${open ? ' open' : ''}`} onClick={onOpen}>
        <span className="ux-money">
          <b>+{rub(fork.profit)} ₽</b>
          <i>{fork.roiPct.toFixed(2)}%</i>
        </span>
        <span className="ux-who">
          <strong>{fork.match}</strong>
          <em>
            {fork.legs[0].name} · {fork.legs[1].name}
          </em>
          {fork.kind === 'ru-ru' && <span className="ux-tag">{fork.kindLabel}</span>}
        </span>
        <span className="ux-meta">
          {fork.sport} · {fork.line === 'live' ? 'лайв' : 'прематч'} · {fork.ageSec} с
        </span>
      </button>
      {open && <Card fork={fork} onToast={onToast} />}
    </div>
  );
}

export function UxScreen(): React.ReactElement {
  const [openId, setOpenId] = useState<string | null>(UX_FORKS[0]?.id ?? null);
  const [toast, setToast] = useState<string | null>(null);
  const onToast = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 2200);
  }, []);
  return (
    <div className="ux">
      <header className="ux-bar">
        <b>FORQ · макет</b>
        <span>не живая лента · пять учебных вилок · цифры выдуманы</span>
        <a href={uxCardHref()}>макеты карточки</a>
      </header>
      <div className="ux-list">
        {UX_FORKS.map((fork) => (
          <Row
            key={fork.id}
            fork={fork}
            open={openId === fork.id}
            onOpen={() => setOpenId((id) => (id === fork.id ? null : fork.id))}
            onToast={onToast}
          />
        ))}
      </div>
      {toast !== null && <div className="ux-toast">{toast}</div>}
    </div>
  );
}
